/**
 * COLONY SLOTS — three independent save files (doc 12 §Saves).
 *
 * A roguelite normally ships one profile, and the strongest argument for three
 * is not variety: it is that onboarding, the first-loss schedule and every
 * balance question have to be play-tested from zero without erasing the
 * colony you actually care about.
 */

import { L, S } from '../../content/strings.ts';
import type { Profile } from '../../meta/types.ts';
import type { SlotId, SlotInfo } from '../../save/store.ts';
import { PixelSurface } from '../../render/surface.ts';
import { drawConfirm, type FrameOpts, type MenuItem } from '../../ui/menu.ts';
import type { Rect } from '../../ui/widgets.ts';
import type { App } from '../app.ts';
import { MenuScreen } from './menuscreen.ts';
import { TitleScreen } from './title.ts';

const NAMES = [S.slots.colonyAlpha, S.slots.colonyBeta, S.slots.colonyGamma];

export class SlotScreen extends MenuScreen {
  private slots: SlotInfo[];
  private confirming: SlotId | null = null;
  private confirmFocus: 0 | 1 = 1;
  private confirmRects: [Rect, Rect] | null = null;

  constructor(app: App) {
    super(app);
    this.slots = app.store.list();
    const at = this.slots.findIndex((s) => s.id === app.slot);
    this.selected = at >= 0 ? at : 0;
  }

  protected frame(): FrameOpts {
    return {
      title: S.slots.colonySlots,
      subtitle: S.slots.threeColoniesDo,
      footer: S.slots.enterPlaysDel,
      stamp: this.app.profile ? `PLAYING ${this.app.profile.name}` : S.slots.noColonyLoaded,
    };
  }

  protected items(): MenuItem[] {
    return this.slots.map((info, k) => {
      const p = info.profile;
      const active = info.id === this.app.slot;
      if (!p) {
        return {
          label: `${k + 1}. EMPTY SLOT`,
          hint: S.slots.startCleanColony,
          value: S.slots.new,
        };
      }
      return {
        label: `${k + 1}. ${p.name}${active ? ' ·' : ''}`,
        hint: summary(p, info.recovered),
        value: L.pearls(p.pearls),
        tone: active ? 'good' : 'ink',
      };
    });
  }

  protected activate(index: number): void {
    const info = this.slots[index];
    if (!info) return;
    const profile = info.profile ?? this.app.store.create(info.id, NAMES[index] ?? S.slots.colony);
    this.app.useProfile(info.id, profile);
    this.app.persist();
    this.app.go((a) => new TitleScreen(a));
  }

  protected override key(k: string): void {
    if (k === 'delete' || k === 'backspace') {
      const info = this.slots[this.selected];
      if (info?.profile) {
        this.confirming = info.id;
        this.confirmFocus = 1;
      }
    }
  }

  protected override back(): void {
    if (this.confirming) {
      this.confirming = null;
      this.dirty = true;
      return;
    }
    if (this.app.profile) this.app.go((a) => new TitleScreen(a));
  }

  override onKey(e: KeyboardEvent): void {
    if (this.confirming) {
      const k = e.key.toLowerCase();
      if (k === 'arrowleft' || k === 'arrowright' || k === 'tab') {
        this.confirmFocus = this.confirmFocus === 0 ? 1 : 0;
      } else if (k === 'enter') {
        if (this.confirmFocus === 0) this.erase();
        else this.confirming = null;
      } else if (k === 'escape') {
        this.confirming = null;
      }
      this.dirty = true;
      return;
    }
    super.onKey(e);
  }

  override onMouseDown(e: MouseEvent): void {
    if (this.confirming && this.confirmRects) {
      const { x, y } = this.app.ui(e);
      const [yes, no] = this.confirmRects;
      if (inside(yes, x, y)) this.erase();
      else if (inside(no, x, y)) this.confirming = null;
      this.dirty = true;
      return;
    }
    super.onMouseDown(e);
  }

  private erase(): void {
    const slot = this.confirming;
    if (!slot) return;
    this.app.store.erase(slot);
    if (this.app.slot === slot) {
      this.app.slot = null;
      this.app.profile = null;
    }
    this.slots = this.app.store.list();
    this.confirming = null;
    this.dirty = true;
  }

  protected override detail(s: PixelSurface, _body: Rect): void {
    if (!this.confirming) {
      this.confirmRects = null;
      return;
    }
    const info = this.slots.find((x) => x.id === this.confirming);
    this.confirmRects = drawConfirm(
      s,
      S.slots.eraseThisColony,
      `${info?.profile?.name ?? ''} · ${info?.profile?.pearls ?? 0} PEARLS · GONE FOR GOOD.`,
      [S.slots.erase, S.slots.keep],
      this.confirmFocus,
    );
  }
}

function summary(p: Profile, recovered: boolean): string {
  const bits = [
    `${p.runs} RUN${p.runs === 1 ? '' : 'S'}`,
    p.wins > 0 ? `${p.wins} CLEARED` : S.slots.neverCleared,
    `${p.owned.length} NODES`,
  ];
  if (p.run) bits.push(S.slots.runProgress);
  if (recovered) bits.push(S.slots.recoveredFromBad);
  return bits.join(' · ');
}

function inside(r: Rect, x: number, y: number): boolean {
  return x >= r.x && y >= r.y && x < r.x + r.w && y < r.y + r.h;
}
